import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Badge,
  HStack,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Code,
  Divider,
  Tooltip,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBug,
  faTrash,
  faWifi,
  faUsers,
  faArrowUp,
  faArrowDown,
  faTachometerAlt,
  faExchangeAlt,
  faHistory,
  faSignal,
  faDownload,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";

// ─── Connection Quality Score ─────────────────────────────────────────────────────

/**
 * Compute an overall connection quality score (0-100) for a user.
 * Factors: idle time, latency skew, throughput, message rate.
 */
function connectionQualityScore(idleMs, skewMs, kbps, msgRate) {
  let score = 100;
  if (idleMs != null) {
    if (idleMs > 120_000) score -= 50;
    else if (idleMs > 60_000) score -= 35;
    else if (idleMs > 30_000) score -= 20;
    else if (idleMs > 10_000) score -= 10;
  }
  if (skewMs != null) {
    if (Math.abs(skewMs) > 15_000) score -= 40;
    else if (Math.abs(skewMs) > 10_000) score -= 25;
    else if (Math.abs(skewMs) > 5_000) score -= 15;
    else if (Math.abs(skewMs) > 2_000) score -= 5;
  }
  if (kbps != null && kbps < 0.01) score -= 20;
  if (msgRate != null && msgRate < 0.1) score -= 10;
  return Math.max(0, Math.min(100, score));
}

function qualityColorScheme(score) {
  if (score >= 80) return "green";
  if (score >= 60) return "blue";
  if (score >= 40) return "yellow";
  if (score >= 20) return "orange";
  return "red";
}

// ─── Formatters ───────────────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes || bytes < 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatKBs(kbs) {
  if (kbs < 0.01) return "0.00";
  return kbs.toFixed(2);
}

function timeAgo(ts) {
  if (!ts) return "—";
  const diff = Date.now() - ts;
  if (diff < 1500) return "just now";
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return new Date(ts).toLocaleTimeString();
}

function statusColorScheme(status) {
  if (!status) return "gray";
  if (status === "connected") return "green";
  if (status === "connecting" || status === "syncing-with-others") return "yellow";
  if (status === "reconnecting") return "orange";
  return "red";
}

const PEER_BW_WINDOW_MS = 5000;

/** Rolling bytes in the last window (for per-peer download KB/s). */
function sumBytesInWindow(buckets, now, windowMs) {
  if (!buckets || !buckets.length) return 0;
  const cutoff = now - windowMs;
  return buckets.filter((b) => b.ts > cutoff).reduce((s, b) => s + (b.bytes || 0), 0);
}

/**
 * Heuristic connectivity for a remote peer (browser cannot read others' NetworkInformation).
 * Uses last packet time, cursor spacing, and peer-announce skew.
 */
function peerConnectivityHint(telemetry, now, presenceRow) {
  const idleMs = telemetry.lastActivityAt ? now - telemetry.lastActivityAt : null;
  const skew = telemetry.lastPeerSkewMs;
  const kbs = telemetry.recvKbps5s;
  const isTyping = !!presenceRow?.isTyping;

  if (idleMs != null && idleMs > 60_000) {
    return { label: "No packets (60s+)", scheme: "red", detail: "May be offline, background tab, or network loss" };
  }
  if (idleMs != null && idleMs > 20_000) {
    return { label: "Stale", scheme: "orange", detail: "No traffic for a while — idle or struggling link" };
  }
  if (skew != null && skew > 12_000) {
    return { label: "High delay / clock skew", scheme: "orange", detail: `Announce ±^${Math.round(skew / 1000)}s (network + clock)` };
  }
  if (telemetry.cursorAvgGapMs != null && telemetry.cursorAvgGapMs > 1500 && isTyping) {
    return { label: "Slow caret updates", scheme: "yellow", detail: `${Math.round(telemetry.cursorAvgGapMs)} ms avg between cursor packets while typing` };
  }
  if (kbs != null && kbs < 0.02 && idleMs != null && idleMs > 12_000) {
    return { label: "Very low traffic", scheme: "gray", detail: "Little data from this peer recently" };
  }
  if (idleMs != null && idleMs > 8000) {
    return { label: "Quiet", scheme: "yellow", detail: "Likely idle" };
  }
  return { label: "OK", scheme: "green", detail: "Receiving updates on time" };
}

function formatMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/** Convert numeric RTT to a descriptive label */
function rttLabel(rtt) {
  if (rtt == null) return null;
  if (rtt < 50) return { label: "Excellent", scheme: "green" };
  if (rtt < 100) return { label: "Good", scheme: "green" };
  if (rtt < 200) return { label: "Fair", scheme: "yellow" };
  if (rtt < 500) return { label: "Poor", scheme: "orange" };
  return { label: "Very Poor", scheme: "red" };
}

/** Signal strength bars (0-4) based on connectivity hint */
function signalBars(hint) {
  if (!hint || !hint.scheme) return 0;
  if (hint.scheme === "green") return 4;
  if (hint.scheme === "yellow") return 3;
  if (hint.scheme === "orange") return 2;
  return 1;
}

function readNavigatorConnection() {
  if (typeof navigator === "undefined") return null;
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return null;
  return {
    effectiveType: c.effectiveType,
    downlink: c.downlink,
    rtt: c.rtt,
    saveData: c.saveData,
  };
}

// ─── Small stat card ──────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, colorScheme = "gray", sub }) {
  const colors = {
    green:  { bg: "green.50",  text: "green.700",  badge: "green" },
    blue:   { bg: "blue.50",   text: "blue.700",   badge: "blue" },
    purple: { bg: "purple.50", text: "purple.700", badge: "purple" },
    orange: { bg: "orange.50", text: "orange.700", badge: "orange" },
    gray:   { bg: "gray.50",   text: "gray.700",   badge: "gray" },
  }[colorScheme] || { bg: "gray.50", text: "gray.700", badge: "gray" };

  return (
    <Box
      flex="1"
      bg={colors.bg}
      borderRadius="md"
      px={3}
      py={2}
      minW="0"
      borderWidth="1px"
      borderColor={`${colorScheme}.200`}
    >
      <HStack spacing={1} mb={0.5}>
        {icon && (
          <Text fontSize="10px" color={colors.text} opacity={0.7}>
            <FontAwesomeIcon icon={icon} />
          </Text>
        )}
        <Text fontSize="10px" color={colors.text} fontWeight="medium" textTransform="uppercase" letterSpacing="0.05em" noOfLines={1}>
          {label}
        </Text>
      </HStack>
      <Text fontSize="md" fontWeight="bold" color={colors.text} lineHeight="1.2">
        {value}
      </Text>
      {sub && (
        <Text fontSize="10px" color={`${colorScheme}.500`} mt={0.5}>{sub}</Text>
      )}
    </Box>
  );
}

// ─── Sparkline (last 30s bandwidth bars) ───────────────────────────────────────────────────

function SparkBars({ buckets, field, color, maxBuckets = 30 }) {
  const recent = [...buckets].slice(-maxBuckets);
  const max = Math.max(...recent.map((b) => b[field] || 0), 1);
  return (
    <HStack spacing="1px" align="flex-end" h="28px" overflow="hidden">
      {recent.map((b, i) => {
        const h = Math.max(2, Math.round(((b[field] || 0) / max) * 28));
        return (
          <Tooltip key={i} label={`${formatBytes(b[field])} — ${new Date(b.ts).toLocaleTimeString()}`} fontSize="xs">
            <Box w="6px" h={`${h}px`} bg={`${color}.400`} borderRadius="1px" flexShrink={0} />
          </Tooltip>
        );
      })}
    </HStack>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────────────

export default function CollaborationDebugPanel({
  isOpen,
  onClose,
  connectionStatus,
  isConnected,
  filePath,
  presenceList = [],
}) {
  // ── Telemetry state ───────────────────────────────────────────────────────
  const msgStatsRef = useRef(new Map()); // `${dir}:${event}` → { count, totalBytes, lastTs }
  const [msgStats, setMsgStats] = useState(new Map());

  const userDataRef = useRef(new Map()); // userId → telemetry + name/color/joinedAt/lastSeen/receivedBytes/status
  const [userData, setUserData] = useState(new Map());

  const [liveFeed, setLiveFeed] = useState([]);

  /** Re-render user rows every second so "time since last packet" and rolling KB/s stay fresh. */
  const [userMetricsTick, setUserMetricsTick] = useState(0);
  const [localNetInfo, setLocalNetInfo] = useState(() => readNavigatorConnection());

  const bandwidthRef = useRef([]); // [{ ts (bucket), sentBytes, receivedBytes }]
  const [bandwidthBuckets, setBandwidthBuckets] = useState([]);
  const [kbsSent, setKbsSent] = useState(0);
  const [kbsReceived, setKbsReceived] = useState(0);

  const [totalSent, setTotalSent] = useState(0);
  const [totalReceived, setTotalReceived] = useState(0);

  const [connHistory, setConnHistory] = useState([]);

  // ── WebSocket provider stats ─────────────────────────────────────────────────
  const [wsStats, setWsStats] = useState({ readyState: null, bufferedAmount: 0, url: null });

  // ── Ping measurement ─────────────────────────────────────────────────────────
  const [pingResult, setPingResult] = useState(null); // { rttMs, ts }
  const [isPinging, setIsPinging] = useState(false);
  const pingInProgressRef = useRef(false);
  const lastPingBroadcastRef = useRef(0);

  // Self-ping auto-refresh: measure RTT every 5s automatically
  const runAutoPing = useCallback(() => {
    const wp = typeof window !== 'undefined' ? window.wsProvider : null;
    if (!wp?.awareness || pingInProgressRef.current) return;
    pingInProgressRef.current = true;
    const sentAt = Date.now();
    const localState = wp.awareness.getLocalState() || {};
    wp.awareness.setLocalStateField('_ping', {
      ts: sentAt,
      seq: (localState._ping?.seq || 0) + 1,
    });

    // Poll until our timestamp echoes back (awareness relay)
    let count = 0;
    const poll = setInterval(() => {
      count++;
      const state = wp.awareness.getLocalState();
      if (state?._ping?.ts === sentAt) {
        clearInterval(poll);
        const rtt = Date.now() - sentAt;
        setPingResult({ rttMs: rtt, ts: Date.now() });
        pingInProgressRef.current = false;
        // Broadcast our measured RTT so peers can see it
        wp.awareness.setLocalStateField('_pingRtt', rtt);
      } else if (count > 30) {
        clearInterval(poll);
        pingInProgressRef.current = false;
      }
    }, 100);
  }, []);

  // Auto-ping every 5s when panel is open
  useEffect(() => {
    if (!isOpen) return;
    runAutoPing(); // immediate
    const id = setInterval(runAutoPing, 5000);
    return () => clearInterval(id);
  }, [isOpen, runAutoPing]);

  // Broadcast our ping timestamp so other users can estimate our latency to them
  const broadcastPingTimestamp = useCallback(() => {
    const wp = typeof window !== 'undefined' ? window.wsProvider : null;
    if (!wp?.awareness) return;
    const now = Date.now();
    if (now - lastPingBroadcastRef.current < 1000) return;
    lastPingBroadcastRef.current = now;
    const localState = wp.awareness.getLocalState() || {};
    wp.awareness.setLocalStateField('_ping', {
      ts: now,
      seq: (localState._ping?.seq || 0) + 1,
    });
  }, []);

  // Listen for peers' ping broadcasts and compute their latency to us
  // Store per-peer latency: peerUserId -> { latencyMs, ts }
  const [peerLatencies, setPeerLatencies] = useState(new Map()); // userId -> { latencyMs, ts }
  const peerLatenciesRef = useRef(new Map());
  const lastPeerPingCheckRef = useRef(0);

  // Update peer latencies by reading awareness states
  // Each peer broadcasts _ping.ts (their sent time) and _pingRtt (their measured RTT)
  const updatePeerLatencies = useCallback(() => {
    const wp = typeof window !== 'undefined' ? window.wsProvider : null;
    if (!wp?.awareness) return;
    const states = wp.awareness.getStates();
    const now = Date.now();
    const localId = wp.awareness.clientID;
    const newLatencies = new Map();

    states.forEach((state, clientId) => {
      if (clientId === localId) return; // Skip self
      // state._pingRtt is the peer self-measured RTT (number, in ms)
      // state._ping.ts is their last broadcast timestamp
      if (state._pingRtt != null) {
        newLatencies.set(clientId, { latencyMs: state._pingRtt, ts: now, isRtt: true });
      } else if (state._ping?.ts) {
        // Fallback: estimate latency from peer to us (not full RTT)
        const latency = now - state._ping.ts;
        newLatencies.set(clientId, { latencyMs: latency, ts: now, isRtt: false });
      }
    });

    peerLatenciesRef.current = newLatencies;
    setPeerLatencies(new Map(newLatencies));
  }, []);

  // Broadcast ping every 2s and update peer latencies every 1s
  useEffect(() => {
    if (!isOpen) return;
    const broadcastId = setInterval(broadcastPingTimestamp, 2000);
    const updateId = setInterval(updatePeerLatencies, 1000);
    // Initial broadcast
    setTimeout(broadcastPingTimestamp, 500);
    return () => {
      clearInterval(broadcastId);
      clearInterval(updateId);
    };
  }, [isOpen, broadcastPingTimestamp, updatePeerLatencies]);

  // Self ping: send awareness with ts, wait for echo back
  const measurePing = useCallback(() => {
    if (pingInProgressRef.current) return;
    const wp = typeof window !== 'undefined' ? window.wsProvider : null;
    if (!wp?.awareness) return;
    pingInProgressRef.current = true;
    setIsPinging(true);
    const sentAt = Date.now();
    // Broadcast a ping field; other clients will echo it via awareness
    // (y-websocket relays awareness to all clients including sender)
    const localState = wp.awareness.getLocalState() || {};
    wp.awareness.setLocalStateField('_ping', { ts: sentAt, seq: (localState._ping?.seq || 0) + 1 });

    // Check if our own awareness update bounces back (this measures full round-trip via server)
    let checkCount = 0;
    const checkHandler = () => {
      checkCount++;
      const state = wp.awareness.getLocalState();
      if (state?._ping?.ts === sentAt) {
        const rtt = Date.now() - sentAt;
        setPingResult({ rttMs: rtt, ts: Date.now() });
        wp.awareness.off('change', checkHandler);
        pingInProgressRef.current = false;
        setIsPinging(false);
      } else if (checkCount > 20) {
        // Timeout after ~2 seconds (polling every 100ms)
        wp.awareness.off('change', checkHandler);
        pingInProgressRef.current = false;
        setIsPinging(false);
        setPingResult({ rttMs: null, ts: Date.now(), error: 'no echo' });
      }
    };
    wp.awareness.on('change', checkHandler);
    // Poll every 100ms
    const pollId = setInterval(() => {
      const state = wp.awareness.getLocalState();
      if (state?._ping?.ts === sentAt) {
        clearInterval(pollId);
        const rtt = Date.now() - sentAt;
        setPingResult({ rttMs: rtt, ts: Date.now() });
        wp.awareness.off('change', checkHandler);
        pingInProgressRef.current = false;
        setIsPinging(false);
      }
    }, 100);
    // Final cleanup
    setTimeout(() => {
      clearInterval(pollId);
      wp.awareness.off('change', checkHandler);
      pingInProgressRef.current = false;
      setIsPinging(false);
    }, 3000);
  }, []);

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    msgStatsRef.current = new Map();
    setMsgStats(new Map());
    userDataRef.current = new Map();
    setUserData(new Map());
    setLiveFeed([]);
    bandwidthRef.current = [];
    setBandwidthBuckets([]);
    setKbsSent(0);
    setKbsReceived(0);
    setTotalSent(0);
    setTotalReceived(0);
    setConnHistory([]);
    msgCountBucketsRef.current = new Map();
    peerLatenciesRef.current = new Map();
    setPeerLatencies(new Map());
  }, []);

  const mergePeerFromRecv = useCallback((userId, d, bytes) => {
    const ts = d.ts || Date.now();
    let u = userDataRef.current.get(userId);
    if (!u) {
      u = {
        name: "Unknown",
        color: "#888",
        joinedAt: null,
        lastSeen: ts,
        receivedBytes: 0,
        status: "online",
        bwBuckets: [],
      };
    }
    const bw = [...(u.bwBuckets || []), { ts, bytes }];
    const cutoff = ts - PEER_BW_WINDOW_MS;
    const pruned = bw.filter((b) => b.ts > cutoff);

    let next = {
      ...u,
      receivedBytes: (u.receivedBytes || 0) + bytes,
      lastSeen: ts,
      lastActivityAt: ts,
      bwBuckets: pruned,
    };

    if (d.event === "cursor") {
      const prev = u.lastCursorAt;
      const gap = prev ? ts - prev : null;
      const cursorAvgGapMs =
        gap != null
          ? u.cursorAvgGapMs == null
            ? gap
            : u.cursorAvgGapMs * 0.65 + gap * 0.35
          : u.cursorAvgGapMs;
      next = { ...next, lastCursorAt: ts, cursorAvgGapMs: cursorAvgGapMs ?? null };
    }

    if (d.event === "collab-peer" && d.peerTs != null) {
      next.lastPeerSkewMs = ts - d.peerTs;
    }

    if (
      d.event === "yjs-update" ||
      d.event === "yjs-sync-step1" ||
      d.event === "yjs-sync-step2"
    ) {
      next.yjsRecvCount = (u.yjsRecvCount || 0) + 1;
    }

    userDataRef.current.set(userId, next);
    setUserData(new Map(userDataRef.current));
  }, []);

  // ── Telemetry listener ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail;
      if (!d) return;

      if (d.direction === "sent" || d.direction === "received") {
        const bytes = d.bytes || 0;

        // Message stats
        const key = `${d.direction}:${d.event}`;
        const cur = msgStatsRef.current.get(key) || { count: 0, totalBytes: 0, lastTs: null };
        msgStatsRef.current.set(key, { count: cur.count + 1, totalBytes: cur.totalBytes + bytes, lastTs: d.ts });
        setMsgStats(new Map(msgStatsRef.current));

        // Totals
        if (d.direction === "sent") setTotalSent((p) => p + bytes);
        else setTotalReceived((p) => p + bytes);

        // Bandwidth buckets (1-second resolution)
        const bucket = Math.floor((d.ts || Date.now()) / 1000) * 1000;
        const bw = bandwidthRef.current;
        const existing = bw.find((b) => b.ts === bucket);
        if (existing) {
          if (d.direction === "sent") existing.sentBytes += bytes;
          else existing.receivedBytes += bytes;
        } else {
          bw.push({
            ts: bucket,
            sentBytes: d.direction === "sent" ? bytes : 0,
            receivedBytes: d.direction === "received" ? bytes : 0,
          });
        }
        // Prune to last 60s
        const cutoff = Date.now() - 60000;
        bandwidthRef.current = bw.filter((b) => b.ts > cutoff);
        setBandwidthBuckets([...bandwidthRef.current]);

        // Per-user received traffic (remote peers only; attributed via userId on wire)
        if (d.direction === "received" && d.userId) {
          mergePeerFromRecv(d.userId, d, bytes);
          trackUserMsgRate(d.userId);
        }

        // Live feed
        setLiveFeed((prev) => [
          { id: `${d.ts}-${Math.random()}`, direction: d.direction, event: d.event, bytes, userId: d.userId, ts: d.ts || Date.now() },
          ...prev,
        ].slice(0, 120));

      } else if (d.type === "user-join") {
        const prev = userDataRef.current.get(d.userId) || {};
        userDataRef.current.set(d.userId, {
          ...prev,
          name: d.name || prev.name || "Unknown",
          color: d.color || prev.color || "#888",
          joinedAt: d.joinedAt || d.ts,
          lastSeen: d.ts,
          receivedBytes: prev.receivedBytes ?? 0,
          status: "online",
          bwBuckets: prev.bwBuckets ?? [],
        });
        setUserData(new Map(userDataRef.current));

      } else if (d.type === "user-leave") {
        const u = userDataRef.current.get(d.userId);
        if (u) {
          userDataRef.current.set(d.userId, { ...u, status: "offline", lastSeen: d.ts });
          setUserData(new Map(userDataRef.current));
        }

      } else if (d.type === "connection") {
        setConnHistory((prev) => [{ status: d.status, ts: d.ts || Date.now() }, ...prev].slice(0, 15));
      }
    };

    window.addEventListener("collab-telemetry", handler);
    return () => window.removeEventListener("collab-telemetry", handler);
  }, [mergePeerFromRecv]);

  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => setUserMetricsTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const c = typeof navigator !== "undefined"
      ? navigator.connection || navigator.mozConnection || navigator.webkitConnection
      : null;
    if (!c) return;
    const sync = () => setLocalNetInfo(readNavigatorConnection());
    sync();
    c.addEventListener("change", sync);
    return () => c.removeEventListener("change", sync);
  }, [isOpen]);

  // ── Rolling KB/s calculation ──────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => {
      const now = Date.now();
      const WINDOW_MS = 5000;
      const recent = bandwidthRef.current.filter((b) => now - b.ts < WINDOW_MS);
      const sent = recent.reduce((s, b) => s + (b.sentBytes || 0), 0);
      const recv = recent.reduce((s, b) => s + (b.receivedBytes || 0), 0);
      setKbsSent((sent / WINDOW_MS) * (1000 / 1024));
      setKbsReceived((recv / WINDOW_MS) * (1000 / 1024));
    }, 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  // ── WebSocket provider stats polling ───────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => {
      const wp = typeof window !== 'undefined' ? window.wsProvider : null;
      if (!wp) return;
      const ws = wp.ws;
      setWsStats({
        readyState: ws?.readyState ?? null,
        bufferedAmount: ws?.bufferedAmount ?? 0,
        url: ws?.url ? new URL(ws.url).host : null,
      });
    }, 2000);
    return () => clearInterval(id);
  }, [isOpen]);

  // Track per-user message counts in rolling 5s windows for throughput calculation
  const msgRateWindowMs = 5000;
  const msgCountBucketsRef = useRef(new Map()); // userId → [{ts, count}]
  
  // Track message rate when receiving from a user
  const trackUserMsgRate = useCallback((userId) => {
    const now = Date.now();
    let buckets = msgCountBucketsRef.current.get(userId) || [];
    // Use 1-second buckets within the window
    const bucketTs = Math.floor(now / 1000) * 1000;
    const last = buckets[buckets.length - 1];
    if (last && last.ts === bucketTs) {
      last.count += 1;
    } else {
      buckets.push({ ts: bucketTs, count: 1 });
    }
    // Prune old buckets
    const cutoff = now - msgRateWindowMs;
    buckets = buckets.filter(b => b.ts > cutoff);
    msgCountBucketsRef.current.set(userId, buckets);
  }, []);

  const getUserMsgRate = useCallback((userId) => {
    const now = Date.now();
    const buckets = msgCountBucketsRef.current.get(userId) || [];
    const cutoff = now - msgRateWindowMs;
    const recent = buckets.filter(b => b.ts > cutoff);
    const total = recent.reduce((s, b) => s + b.count, 0);
    return (total / msgRateWindowMs) * 1000; // msgs per second
  }, []);

  // ── Merge presenceList into userData (for users who were present before debug opened) ──
  useEffect(() => {
    if (!presenceList || presenceList.length === 0) return;
    presenceList.forEach((u) => {
      if (!u.userId || u.isCurrentUser) return;
      const existing = userDataRef.current.get(u.userId);
      if (!existing) {
        userDataRef.current.set(u.userId, {
          name: u.name || "Unknown",
          color: u.color || "#888",
          joinedAt: u.joinedAt || null,
          lastSeen: Date.now(),
          receivedBytes: 0,
          status: "online",
          bwBuckets: [],
        });
      } else {
        userDataRef.current.set(u.userId, {
          ...existing,
          status: "online",
          name: u.name || existing.name,
          color: u.color || existing.color,
        });
      }
    });
    setUserData(new Map(userDataRef.current));
  }, [presenceList]);

  // ── Derived values ────────────────────────────────────────────────────────
  const onlineCount = presenceList.filter((u) => !u.isCurrentUser).length;
  const channelName = filePath ? `doc-collab:${encodeURIComponent(filePath)}` : "—";
  const channelDisplayName = filePath ? `doc-collab:${filePath.split("/").slice(-2).join("/")}` : "—";

  // Sort message stats: sent first then received, alphabetical event name
  const sortedMsgStats = [...msgStats.entries()].sort(([a], [b]) => {
    const [aDir, aEv] = a.split(":");
    const [bDir, bEv] = b.split(":");
    if (aDir !== bDir) return aDir === "sent" ? -1 : 1;
    return aEv.localeCompare(bEv);
  });

  // All users to show in Users tab (merge telemetry + presenceList for "You")
  const currentUser = presenceList.find((u) => u.isCurrentUser);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent maxWidth="800px" maxH="90vh" borderRadius="xl" overflow="hidden">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <ModalHeader
          py={3}
          px={4}
          borderBottomWidth="1px"
          borderColor="gray.200"
          bg="gray.50"
        >
          <HStack justify="space-between" align="center">
            <HStack spacing={2}>
              <Text color="gray.500" fontSize="sm">
                <FontAwesomeIcon icon={faBug} />
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="gray.800">
                Realtime Collab Debug
              </Text>
              <Badge
                colorScheme={statusColorScheme(connectionStatus)}
                borderRadius="full"
                px={2}
                py={0.5}
                fontSize="10px"
                fontWeight="bold"
              >
                {connectionStatus || "unknown"}
              </Badge>
            </HStack>
            <HStack spacing={2}>
              <Tooltip label={channelName} fontSize="xs">
                <Text fontSize="10px" color="gray.400" fontFamily="mono" maxW="260px" noOfLines={1}>
                  {channelDisplayName}
                </Text>
              </Tooltip>
              <Button
                size="xs"
                variant="outline"
                colorScheme="red"
                leftIcon={<FontAwesomeIcon icon={faTrash} />}
                onClick={handleReset}
                fontSize="10px"
                h="22px"
              >
                Reset
              </Button>
              <Tooltip label="Export connection stats as JSON" fontSize="xs">
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={() => {
                    const data = {
                      exportedAt: new Date().toISOString(),
                      connectionStatus,
                      isConnected,
                      channelName,
                      localNetInfo,
                      wsStats,
                      totalSent,
                      totalReceived,
                      kbsSent,
                      kbsReceived,
                      onlineCount,
                      pingResult,
                      wsStats,
                      users: [...userData.entries()].map(([id, u]) => ({
                        userId: id,
                        name: u.name,
                        color: u.color,
                        status: u.status,
                        joinedAt: u.joinedAt,
                        lastSeen: u.lastSeen,
                        idleMs: u.lastActivityAt ? Date.now() - u.lastActivityAt : null,
                        skewMs: u.lastPeerSkewMs,
                        cursorAvgGapMs: u.cursorAvgGapMs,
                        receivedBytes: u.receivedBytes,
                        yjsRecvCount: u.yjsRecvCount,
                      })),
                      peerLatencies: [...peerLatencies.entries()].map(([cid, lat]) => ({ clientId: cid, ...lat })),
                      msgStats: [...msgStats.entries()].map(([k, v]) => ({ key: k, ...v })),
                      connHistory,
                    };
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `collab-debug-${Date.now()}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  fontSize="10px"
                  h="22px"
                >
                  Export
                </Button>
              </Tooltip>
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={3} />

        {/* ── Stats bar ──────────────────────────────────────────────────── */}
        <Box px={4} py={2} borderBottomWidth="1px" borderColor="gray.100" bg="white">
          <HStack spacing={2}>
            <StatCard
              label="Users online"
              value={onlineCount}
              icon={faUsers}
              colorScheme="blue"
              sub={onlineCount === 0 ? "solo editing" : `${onlineCount} collaborator${onlineCount > 1 ? "s" : ""}`}
            />
            <StatCard
              label="↑ Sent"
              value={formatBytes(totalSent)}
              icon={faArrowUp}
              colorScheme="green"
              sub={`${formatKBs(kbsSent)} KB/s`}
            />
            <StatCard
              label="↓ Received"
              value={formatBytes(totalReceived)}
              icon={faArrowDown}
              colorScheme="purple"
              sub={`${formatKBs(kbsReceived)} KB/s`}
            />
            {localNetInfo && localNetInfo.rtt != null && (
              <StatCard
                label="Local RTT"
                value={`${localNetInfo.rtt} ms`}
                icon={faSignal}
                colorScheme={localNetInfo.rtt < 100 ? "green" : localNetInfo.rtt < 200 ? "yellow" : localNetInfo.rtt < 500 ? "orange" : "red"}
                sub={localNetInfo.effectiveType || ""}
              />
            )}
            <StatCard
              label="Ping"
              value={pingResult?.rttMs != null ? `${pingResult.rttMs} ms` : "—"}
              icon={faSyncAlt}
              colorScheme={pingResult?.rttMs == null ? "gray" : pingResult.rttMs < 100 ? "green" : pingResult.rttMs < 300 ? "yellow" : "orange"}
              sub={
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="blue"
                  leftIcon={<FontAwesomeIcon icon={faSyncAlt} />}
                  onClick={measurePing}
                  isDisabled={isPinging}
                  h="16px"
                  fontSize="9px"
                  pt={0}
                  mt={-0.5}
                >
                  {isPinging ? "pinging…" : "ping"}
                </Button>
              }
            />
            <StatCard
              label="Total traffic"
              value={formatBytes(totalSent + totalReceived)}
              icon={faExchangeAlt}
              colorScheme={isConnected ? "green" : "orange"}
              sub={isConnected ? "connected" : "disconnected"}
            />
          </HStack>
        </Box>

        <ModalBody p={0} overflowY="auto">
          <Tabs variant="line" colorScheme="blue" size="sm">
            <TabList px={4} borderBottomWidth="1px" borderColor="gray.200" bg="gray.50">
              <Tab fontSize="xs" fontWeight="medium" py={2}>
                <HStack spacing={1}>
                  <FontAwesomeIcon icon={faTachometerAlt} />
                  <Text>Bandwidth</Text>
                </HStack>
              </Tab>
              <Tab fontSize="xs" fontWeight="medium" py={2}>
                <HStack spacing={1}>
                  <FontAwesomeIcon icon={faExchangeAlt} />
                  <Text>Messages</Text>
                  {msgStats.size > 0 && (
                    <Badge colorScheme="blue" borderRadius="full" fontSize="9px" px={1}>{msgStats.size}</Badge>
                  )}
                </HStack>
              </Tab>
              <Tab fontSize="xs" fontWeight="medium" py={2}>
                <HStack spacing={1}>
                  <FontAwesomeIcon icon={faUsers} />
                  <Text>Users</Text>
                  {onlineCount > 0 && (
                    <Badge colorScheme="green" borderRadius="full" fontSize="9px" px={1}>{onlineCount}</Badge>
                  )}
                </HStack>
              </Tab>
              <Tab fontSize="xs" fontWeight="medium" py={2}>
                <HStack spacing={1}>
                  <FontAwesomeIcon icon={faHistory} />
                  <Text>Live Feed</Text>
                  {liveFeed.length > 0 && (
                    <Badge colorScheme="gray" borderRadius="full" fontSize="9px" px={1}>{liveFeed.length}</Badge>
                  )}
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>

              {/* ── Bandwidth tab ─────────────────────────────────────────── */}
              <TabPanel px={4} py={3}>
                <VStack align="stretch" spacing={4}>

                  {/* KB/s gauges */}
                  <HStack spacing={4}>
                    <Box flex="1" bg="green.50" borderRadius="md" p={3} borderWidth="1px" borderColor="green.200">
                      <Text fontSize="10px" color="green.600" fontWeight="bold" textTransform="uppercase" mb={1}>
                        <FontAwesomeIcon icon={faArrowUp} /> Upload KB/s (5s avg)
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="green.700" lineHeight="1">
                        {formatKBs(kbsSent)}
                      </Text>
                      <Text fontSize="10px" color="green.500" mt={1}>KB/s</Text>
                    </Box>
                    <Box flex="1" bg="purple.50" borderRadius="md" p={3} borderWidth="1px" borderColor="purple.200">
                      <Text fontSize="10px" color="purple.600" fontWeight="bold" textTransform="uppercase" mb={1}>
                        <FontAwesomeIcon icon={faArrowDown} /> Download KB/s (5s avg)
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="purple.700" lineHeight="1">
                        {formatKBs(kbsReceived)}
                      </Text>
                      <Text fontSize="10px" color="purple.500" mt={1}>KB/s</Text>
                    </Box>
                  </HStack>

                  {/* Sparklines */}
                  {bandwidthBuckets.length > 0 ? (
                    <VStack align="stretch" spacing={2}>
                      <Box bg="white" borderRadius="md" p={3} borderWidth="1px" borderColor="green.100">
                        <Text fontSize="10px" color="green.600" fontWeight="medium" mb={2}>
                          ↑ Upload — last {Math.min(bandwidthBuckets.length, 30)}s
                        </Text>
                        <SparkBars buckets={bandwidthBuckets} field="sentBytes" color="green" />
                      </Box>
                      <Box bg="white" borderRadius="md" p={3} borderWidth="1px" borderColor="purple.100">
                        <Text fontSize="10px" color="purple.600" fontWeight="medium" mb={2}>
                          ↓ Download — last {Math.min(bandwidthBuckets.length, 30)}s
                        </Text>
                        <SparkBars buckets={bandwidthBuckets} field="receivedBytes" color="purple" />
                      </Box>
                    </VStack>
                  ) : (
                    <Box textAlign="center" py={6}>
                      <Text color="gray.400" fontSize="sm">No bandwidth data yet.</Text>
                      <Text color="gray.400" fontSize="xs" mt={1}>Start editing or wait for peers to send updates.</Text>
                    </Box>
                  )}

                  {/* Connection history */}
                  {connHistory.length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>
                          Connection History
                        </Text>
                        <VStack align="stretch" spacing={1}>
                          {connHistory.map((e, i) => (
                            <HStack key={i} justify="space-between">
                              <Badge
                                colorScheme={
                                  e.status === "connected" ? "green" :
                                  e.status === "reconnecting" ? "orange" : "red"
                                }
                                fontSize="10px"
                              >
                                {e.status}
                              </Badge>
                              <Text fontSize="10px" color="gray.500">
                                {new Date(e.ts).toLocaleTimeString()}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </>
                  )}
                </VStack>
              </TabPanel>

              {/* ── Messages tab ──────────────────────────────────────────── */}
              <TabPanel px={4} py={3}>
                {sortedMsgStats.length === 0 ? (
                  <Box textAlign="center" py={8}>
                    <Text color="gray.400" fontSize="sm">No messages tracked yet.</Text>
                    <Text color="gray.400" fontSize="xs" mt={1}>
                      Events appear once the Yjs channel is active and peers exchange data.
                    </Text>
                  </Box>
                ) : (
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th fontSize="10px" py={2}>Event</Th>
                          <Th fontSize="10px" py={2}>Dir</Th>
                          <Th fontSize="10px" py={2} isNumeric>Count</Th>
                          <Th fontSize="10px" py={2} isNumeric>Total</Th>
                          <Th fontSize="10px" py={2} isNumeric>Avg / msg</Th>
                          <Th fontSize="10px" py={2}>Last seen</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {sortedMsgStats.map(([key, stats]) => {
                          const [dir, event] = key.split(":");
                          const avg = stats.count > 0 ? Math.round(stats.totalBytes / stats.count) : 0;
                          return (
                            <Tr key={key} _hover={{ bg: "gray.50" }}>
                              <Td py={1.5}>
                                <Code fontSize="10px" colorScheme={dir === "sent" ? "green" : "purple"}>
                                  {event}
                                </Code>
                              </Td>
                              <Td py={1.5}>
                                <Badge
                                  colorScheme={dir === "sent" ? "green" : "purple"}
                                  fontSize="9px"
                                  borderRadius="full"
                                  px={1.5}
                                >
                                  {dir === "sent" ? "↑ sent" : "↓ recv"}
                                </Badge>
                              </Td>
                              <Td py={1.5} isNumeric>
                                <Text fontSize="xs" fontWeight="medium">{stats.count.toLocaleString()}</Text>
                              </Td>
                              <Td py={1.5} isNumeric>
                                <Text fontSize="xs" fontWeight="medium">{formatBytes(stats.totalBytes)}</Text>
                              </Td>
                              <Td py={1.5} isNumeric>
                                <Text fontSize="xs" color="gray.500">{formatBytes(avg)}</Text>
                              </Td>
                              <Td py={1.5}>
                                <Text fontSize="10px" color="gray.400">{timeAgo(stats.lastTs)}</Text>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>

                    {/* Summary footer */}
                    <Box mt={3} p={2} bg="gray.50" borderRadius="md">
                      <HStack spacing={4}>
                        <Text fontSize="10px" color="gray.500">
                          Total msgs:{" "}
                          <Text as="span" fontWeight="bold" color="gray.700">
                            {[...msgStats.values()].reduce((s, v) => s + v.count, 0).toLocaleString()}
                          </Text>
                        </Text>
                        <Text fontSize="10px" color="green.600">
                          ↑ {formatBytes(totalSent)}
                        </Text>
                        <Text fontSize="10px" color="purple.600">
                          ↓ {formatBytes(totalReceived)}
                        </Text>
                        <Text fontSize="10px" color="gray.500">
                          Combined: {formatBytes(totalSent + totalReceived)}
                        </Text>
                      </HStack>
                    </Box>
                  </Box>
                )}
              </TabPanel>

              {/* ── Users tab ─────────────────────────────────────────────── */}
              <TabPanel px={4} py={3}>
                {presenceList.length === 0 && userData.size === 0 ? (
                  <Box textAlign="center" py={8}>
                    <Text color="gray.400" fontSize="sm">No collaborators detected.</Text>
                    <Text color="gray.400" fontSize="xs" mt={1}>
                      Open this document in another tab or invite someone to collaborate.
                    </Text>
                  </Box>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {/* Current user */}
                    {currentUser && (
                      <Box p={3} borderRadius="md" borderWidth="1px" borderColor="blue.200" bg="blue.50">
                        <HStack justify="space-between" align="flex-start">
                          <HStack spacing={2}>
                            <Box
                              w="10px" h="10px" borderRadius="full" flexShrink={0}
                              bg={currentUser.color || "blue.400"}
                              boxShadow={`0 0 0 2px white, 0 0 0 3px ${currentUser.color || "#3182ce"}`}
                            />
                            <VStack align="start" spacing={0}>
                              <HStack spacing={2}>
                                <Text fontSize="sm" fontWeight="bold" color="blue.700">
                                  {currentUser.name || "You"}
                                </Text>
                                <Badge colorScheme="blue" fontSize="9px" borderRadius="full">You</Badge>
                                {/* Connection quality badge */}
                                {localNetInfo && localNetInfo.rtt != null && (
                                  <Tooltip label={`Est. RTT: ${localNetInfo.rtt}ms via Network Information API`} fontSize="xs">
                                    <Badge
                                      colorScheme={rttLabel(localNetInfo.rtt)?.scheme || "gray"}
                                      fontSize="9px"
                                      borderRadius="full"
                                      variant="subtle"
                                      cursor="help"
                                    >
                                      <HStack spacing={1} as="span">
                                        <FontAwesomeIcon icon={faSignal} />
                                        <Text as="span">{rttLabel(localNetInfo.rtt)?.label || "Unknown"}</Text>
                                      </HStack>
                                    </Badge>
                                  </Tooltip>
                                )}
                              </HStack>
                              {localNetInfo && (
                                <HStack spacing={2} mt={0.5} flexWrap="wrap">
                                  <Text fontSize="10px" color="blue.600">
                                    <FontAwesomeIcon icon={faWifi} />{" "}
                                    {localNetInfo.effectiveType && `${localNetInfo.effectiveType}`}
                                  </Text>
                                  {pingResult?.rttMs != null && (
                                    <Text fontSize="10px" color={pingResult.rttMs < 150 ? "green.500" : "orange.500"}>
                                      · ping {pingResult.rttMs} ms
                                    </Text>
                                  )}
                                  {localNetInfo.downlink != null && (
                                    <Text fontSize="10px" color="blue.600">
                                      {localNetInfo.downlink} Mb/s down
                                    </Text>
                                  )}
                                </HStack>
                              )}
                            </VStack>
                          </HStack>
                          <VStack align="flex-end" spacing={1}>
                            <Text fontSize="10px" color="blue.500" textAlign="right">
                              ↑ {formatBytes(totalSent)} sent · {formatKBs(kbsSent)} KB/s
                            </Text>
                            <Text fontSize="10px" color="blue.500" textAlign="right">
                              ↓ {formatBytes(totalReceived)} total recv
                            </Text>
                            {pingResult?.rttMs != null && (
                              <Text fontSize="10px" color={pingResult.rttMs < 150 ? "green.500" : "orange.500"} textAlign="right" fontWeight="bold">
                                RTT {pingResult.rttMs} ms
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                      </Box>
                    )}

                    {/* Remote users from presenceList */}
                    {presenceList.filter((u) => !u.isCurrentUser).map((u) => {
                      void userMetricsTick;
                      const now = Date.now();
                      const telemetry = userData.get(u.userId) || {};
                      const peerKbps =
                        (sumBytesInWindow(telemetry.bwBuckets, now, PEER_BW_WINDOW_MS) / PEER_BW_WINDOW_MS) *
                        (1000 / 1024);
                      const msgRate = getUserMsgRate(u.userId);
                      const skewMs = telemetry.lastPeerSkewMs;
                      const idleMs = telemetry.lastActivityAt ? now - telemetry.lastActivityAt : null;
                      const hint = peerConnectivityHint({ ...telemetry, recvKbps5s: peerKbps }, now, u);
                      const qualityScore = connectionQualityScore(idleMs, skewMs, peerKbps, msgRate);
                      const bars = signalBars(hint);
                      return (
                        <Box key={u.userId} p={3} borderRadius="md" borderWidth="1px" borderColor="gray.200" bg="white" _hover={{ bg: "gray.50" }}>
                          <HStack justify="space-between" align="flex-start" spacing={3}>
                            <HStack spacing={2} align="flex-start">
                              <Box
                                w="10px" h="10px" borderRadius="full" flexShrink={0} mt={1}
                                bg={u.color || "#888"}
                                boxShadow={`0 0 0 2px white, 0 0 0 3px ${u.color || "#888"}`}
                              />
                              <VStack align="start" spacing={1}>
                                <HStack spacing={2} flexWrap="wrap">
                                  <Text fontSize="sm" fontWeight="medium">{u.name || "Unknown"}</Text>
                                  <Badge colorScheme="green" fontSize="9px" borderRadius="full" variant="subtle">
                                    online
                                  </Badge>
                                  {/* Connection quality score badge */}
                                  <Tooltip label={`Connection quality score: ${qualityScore}/100`} fontSize="xs">
                                    <Badge
                                      colorScheme={qualityColorScheme(qualityScore)}
                                      fontSize="9px"
                                      borderRadius="full"
                                      variant="subtle"
                                      cursor="help"
                                    >
                                      <HStack spacing={1} as="span">
                                        <FontAwesomeIcon icon={faSignal} />
                                        <Text as="span">{qualityScore}</Text>
                                      </HStack>
                                    </Badge>
                                  </Tooltip>
                                  <Tooltip label={hint.detail} fontSize="xs" hasArrow>
                                    <Badge
                                      colorScheme={hint.scheme}
                                      fontSize="9px"
                                      borderRadius="full"
                                      variant="subtle"
                                      cursor="help"
                                    >
                                      <HStack spacing={1} as="span">
                                        <FontAwesomeIcon icon={faSignal} />
                                        <Text as="span">{hint.label}</Text>
                                      </HStack>
                                    </Badge>
                                  </Tooltip>
                                  {u.presenceViaBroadcast && (
                                    <Badge colorScheme="gray" fontSize="9px" variant="outline">roster broadcast</Badge>
                                  )}
                                  {u.isTyping && (
                                    <Badge colorScheme="orange" fontSize="9px" variant="subtle">typing</Badge>
                                  )}
                                </HStack>

                                {/* Signal bars + latency + skew row */}
                                <HStack spacing={3} flexWrap="wrap">
                                  {/* Signal strength bars */}
                                  <Tooltip label={`Connection quality: ${bars}/4 bars`} fontSize="xs">
                                    <HStack spacing="2px" flexShrink={0}>
                                      {[1, 2, 3, 4].map((bar) => (
                                        <Box
                                          key={bar}
                                          w="4px"
                                          borderRadius="1px"
                                          bg={bar <= bars ? (bars >= 3 ? "green.400" : bars === 2 ? "orange.400" : "red.400") : "gray.200"}
                                          h={`${bar * 3 + 2}px`}
                                        />
                                      ))}
                                    </HStack>
                                  </Tooltip>

                                  {/* Latency (from peer skew, lower is better) */}
                                  {skewMs != null && (
                                    <Tooltip label={`Announce latency ±${formatMs(Math.abs(skewMs))} — clock + network skew`} fontSize="xs">
                                      <HStack spacing={1} flexShrink={0}>
                                        <Text fontSize="10px" fontWeight="medium" color={skewMs > 5000 ? "red.500" : skewMs > 2000 ? "orange.500" : "green.600"}>
                                          {formatMs(Math.abs(skewMs))}
                                        </Text>
                                        <Text fontSize="9px" color="gray.400">± latency</Text>
                                      </HStack>
                                    </Tooltip>
                                  )}

                                  {/* Cursor gap */}
                                  {telemetry.cursorAvgGapMs != null && (
                                    <Tooltip label={`Avg gap ${formatMs(telemetry.cursorAvgGapMs)} between cursor packets`} fontSize="xs">
                                      <HStack spacing={1} flexShrink={0}>
                                        <Text fontSize="10px" color="gray.500">
                                          {formatMs(telemetry.cursorAvgGapMs)} gap
                                        </Text>
                                      </HStack>
                                    </Tooltip>
                                  )}

                                  {/* Idle time */}
                                  {idleMs != null && (
                                    <Text fontSize="10px" color={idleMs > 30000 ? "orange.400" : "gray.400"}>
                                      idle {formatMs(idleMs)}
                                    </Text>
                                  )}
                                </HStack>

                                <Text fontSize="10px" color="gray.600" lineHeight="1.4">
                                  <FontAwesomeIcon icon={faWifi} /> Last packet{" "}
                                  {idleMs != null ? `${formatMs(idleMs)} ago` : "—"}
                                  {telemetry.lastPeerSkewMs != null && (
                                    <> · announce ± {formatMs(telemetry.lastPeerSkewMs)}</>
                                  )}
                                  {/* Show this user's ping/RTT (from their _pingRtt broadcast or _ping.ts estimation) */}
                                  {(() => {
                                    // Find this user's clientId in awareness to get their latency
                                    const wp = typeof window !== 'undefined' ? window.wsProvider : null;
                                    if (!wp?.awareness) return null;
                                    const states = wp.awareness.getStates();
                                    for (const [cid, state] of states) {
                                      if (cid === wp.awareness.clientID) continue;
                                      if (state.user?.id === u.userId || state.user?.userId === u.userId) {
                                        const lat = peerLatencies.get(cid);
                                        if (lat?.latencyMs != null) {
                                          const isRtt = lat.isRtt;
                                          return (
                                            <Text as="span" color={isRtt ? "green.500" : "blue.500"} fontSize="10px" fontWeight={isRtt ? "bold" : "normal"}>
                                              {" "}· {isRtt ? "RTT" : "latency"} {formatMs(lat.latencyMs)}
                                            </Text>
                                          );
                                        }
                                      }
                                    }
                                    return null;
                                  })()}
                                </Text>
                                <Text fontSize="10px" color="gray.500" lineHeight="1.4">
                                  ↓ {formatKBs(peerKbps)} KB/s (5s) · {msgRate.toFixed(1)} msg/s · total {formatBytes(telemetry.receivedBytes || 0)}
                                  {telemetry.yjsRecvCount > 0 && ` · ${telemetry.yjsRecvCount} Yjs recv`}
                                </Text>
                                {u.cursor && typeof u.cursor.index === "number" && (
                                  <Text fontSize="10px" color="gray.400" fontFamily="mono">
                                    caret pos {u.cursor.index}
                                  </Text>
                                )}
                                {(telemetry.joinedAt || telemetry.lastSeen) && (
                                  <Text fontSize="10px" color="gray.400">
                                    {telemetry.joinedAt && <>Joined {timeAgo(telemetry.joinedAt)}</>}
                                    {telemetry.joinedAt && telemetry.lastSeen && " · "}
                                    {telemetry.lastSeen && <>last active {timeAgo(telemetry.lastSeen)}</>}
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                          </HStack>
                        </Box>
                      );
                    })}

                    {/* Online users from telemetry (when presenceList is empty or missing users) */}
                    {[...userData.entries()]
                      .filter(([uid, u]) => u.status === "online" && !presenceList.find((p) => p.userId === uid))
                      .map(([uid, u]) => {
                        void userMetricsTick;
                        const now = Date.now();
                        const telemetry = u;
                        const peerKbps =
                          (sumBytesInWindow(telemetry.bwBuckets, now, PEER_BW_WINDOW_MS) / PEER_BW_WINDOW_MS) *
                          (1000 / 1024);
                        const skewMs = telemetry.lastPeerSkewMs;
                        return (
                          <Box key={uid} p={3} borderRadius="md" borderWidth="1px" borderColor="gray.200" bg="white" _hover={{ bg: "gray.50" }}>
                            <HStack justify="space-between" align="flex-start" spacing={3}>
                              <HStack spacing={2} align="flex-start">
                                <Box
                                  w="10px" h="10px" borderRadius="full" flexShrink={0} mt={1}
                                  bg={u.color || "#888"}
                                  boxShadow={`0 0 0 2px white, 0 0 0 3px ${u.color || "#888"}`}
                                />
                                <VStack align="start" spacing={1}>
                                  <HStack spacing={2} flexWrap="wrap">
                                    <Text fontSize="sm" fontWeight="medium">{u.name || "Unknown"}</Text>
                                    <Badge colorScheme="green" fontSize="9px" borderRadius="full" variant="subtle">
                                      online
                                    </Badge>
                                    <Badge colorScheme="purple" fontSize="9px" borderRadius="full" variant="subtle">
                                      telemetry
                                    </Badge>
                                    {skewMs != null && (
                                      <Tooltip label={`Announce latency ±${formatMs(Math.abs(skewMs))}`} fontSize="xs">
                                        <Badge
                                          colorScheme={Math.abs(skewMs) > 5000 ? "red" : Math.abs(skewMs) > 2000 ? "orange" : "green"}
                                          fontSize="9px"
                                          borderRadius="full"
                                          variant="subtle"
                                          cursor="help"
                                        >
                                          ± {formatMs(Math.abs(skewMs))}
                                        </Badge>
                                      </Tooltip>
                                    )}
                                  </HStack>
                                  <Text fontSize="10px" color="gray.500">
                                    ↓ {formatKBs(peerKbps)} KB/s (5s) · total {formatBytes(telemetry.receivedBytes || 0)}
                                    {telemetry.yjsRecvCount > 0 && ` · ${telemetry.yjsRecvCount} Yjs recv`}
                                  </Text>
                                  {(telemetry.joinedAt || telemetry.lastSeen) && (
                                    <Text fontSize="10px" color="gray.400">
                                      {telemetry.joinedAt && <>Joined {timeAgo(telemetry.joinedAt)}</>}
                                      {telemetry.joinedAt && telemetry.lastSeen && " · "}
                                      {telemetry.lastSeen && <>last active {timeAgo(telemetry.lastSeen)}</>}
                                    </Text>
                                  )}
                                </VStack>
                              </HStack>
                            </HStack>
                          </Box>
                        );
                      })}

                    {/* Offline users from telemetry that left */}
                    {[...userData.entries()]
                      .filter(([uid, u]) => u.status === "offline" && !presenceList.find((p) => p.userId === uid))
                      .map(([uid, u]) => (
                        <Box key={uid} p={3} borderRadius="md" borderWidth="1px" borderColor="gray.100" bg="gray.50" opacity={0.6}>
                          <HStack justify="space-between">
                            <HStack spacing={2}>
                              <Box w="10px" h="10px" borderRadius="full" bg={u.color || "#888"} opacity={0.4} flexShrink={0} />
                              <Text fontSize="sm" color="gray.500">{u.name || "Unknown"}</Text>
                              <Badge colorScheme="gray" fontSize="9px" borderRadius="full" variant="subtle">left</Badge>
                            </HStack>
                            <Text fontSize="10px" color="gray.400">
                              {u.receivedBytes > 0 ? `↓ ${formatBytes(u.receivedBytes)}` : ""}
                              {u.lastSeen ? ` · left ${timeAgo(u.lastSeen)}` : ""}
                            </Text>
                          </HStack>
                        </Box>
                      ))}
                  </VStack>
                )}
              </TabPanel>

              {/* ── Live Feed tab ─────────────────────────────────────────── */}
              <TabPanel px={4} py={3}>
                <HStack justify="space-between" mb={3}>
                  <Text fontSize="xs" color="gray.500">
                    Last {liveFeed.length} events (max 120)
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    leftIcon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => setLiveFeed([])}
                    h="22px"
                    fontSize="10px"
                  >
                    Clear
                  </Button>
                </HStack>

                {liveFeed.length === 0 ? (
                  <Box textAlign="center" py={8}>
                    <Text color="gray.400" fontSize="sm">No events yet.</Text>
                    <Text color="gray.400" fontSize="xs" mt={1}>Events appear in real-time as messages are sent and received.</Text>
                  </Box>
                ) : (
                  <Box
                    maxH="420px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="md"
                    fontFamily="mono"
                    fontSize="11px"
                  >
                    {liveFeed.map((ev) => (
                      <HStack
                        key={ev.id}
                        px={3}
                        py={1}
                        spacing={2}
                        borderBottomWidth="1px"
                        borderColor="gray.50"
                        _hover={{ bg: "gray.50" }}
                        align="center"
                      >
                        <Badge
                          colorScheme={ev.direction === "sent" ? "green" : "purple"}
                          fontSize="9px"
                          variant="subtle"
                          minW="36px"
                          textAlign="center"
                        >
                          {ev.direction === "sent" ? "↑ out" : "↓ in"}
                        </Badge>
                        <Text
                          fontSize="10px"
                          color={ev.direction === "sent" ? "green.700" : "purple.700"}
                          fontWeight="medium"
                          minW="110px"
                        >
                          {ev.event}
                        </Text>
                        <Text
                          fontSize="10px"
                          color={ev.bytes > 10240 ? "orange.500" : "gray.600"}
                          fontWeight={ev.bytes > 10240 ? "bold" : "normal"}
                          minW="60px"
                        >
                          {formatBytes(ev.bytes)}
                        </Text>
                        {ev.userId && (
                          <Text fontSize="9px" color="gray.400" noOfLines={1} maxW="100px">
                            {ev.userId.substring(0, 8)}…
                          </Text>
                        )}
                        <Text fontSize="9px" color="gray.400" ml="auto" flexShrink={0}>
                          {new Date(ev.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 2 })}
                        </Text>
                      </HStack>
                    ))}
                  </Box>
                )}
              </TabPanel>

            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

/**
 * Small toggle button component that shows a bug icon.
 * Manages its own open/closed state for the debug panel.
 * Place this under the Save button in EditPatientModal.
 */
export function CollaborationDebugToggle({ 
  /** Props passed to the underlying panel */
  connectionStatus,
  isConnected,
  filePath,
  presenceList = [],
}) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <Button
        size="xs"
        variant="ghost"
        colorScheme="gray"
        leftIcon={<FontAwesomeIcon icon={faBug} />}
        onClick={() => setIsOpen(true)}
        h="28px"
        fontSize="10px"
        mt={1}
        w="full"
      >
        Debug Collab
      </Button>
      <CollaborationDebugPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        connectionStatus={connectionStatus}
        isConnected={isConnected}
        filePath={filePath}
        presenceList={presenceList}
      />
    </>
  );
}
